import { ChangeDetectorRef, Component, DestroyRef, OnInit, ViewEncapsulation, inject, input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { LoadingSpinnerService, ToastMessageService } from '@k12/app-services';
import { ToastMessageType } from '@k12/models';
import { AwardsService } from '../../../awards/awards.service';
import { StudentLedgerTransaction } from '../../../awards/models/student-ledger-transaction.model';

export interface StudentTransaction {
  distributionDate: Date | null;
  academicYear: string;
  school: string;
  program: string;
  term: string;
  distributedAmount: number;
  refundAmount: number | null;
  refundDate: Date | null;
}

@Component({
  selector: 'lib-student-ledger-detail',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, CurrencyPipe, DatePipe, CfiMatTableComponent],
  templateUrl: './student-ledger-detail.html',
  styleUrls: ['./student-ledger-detail.scss']
})
export class StudentLedgerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly awardsService = inject(AwardsService);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);

  readonly academicYearId = input<number | null>(null);
  private readonly academicYearIdInput$ = toObservable(this.academicYearId);
  transactions: StudentTransaction[] = [];

  readonly columnDefinitions: ColumnDefinition[] = [
    { name: 'distributionDate', header: 'Disbursement Date', sortable: true, width: 13 },
    { name: 'academicYear', header: 'Academic Year', sortable: true, width: 13 },
    { name: 'school', header: 'School', sortable: true, width: 20 },
    { name: 'program', header: 'Program', sortable: true, width: 13 },
    { name: 'term', header: 'Term', sortable: true, width: 11 },
    { name: 'distributedAmount', header: 'Amount', sortable: true, width: 12 },
    { name: 'refundAmount', header: 'Refund Amount', sortable: true, width: 12 },
    { name: 'refundDate', header: 'Refund Date', sortable: true, width: 13 }
  ];

  readonly displayedColumns: string[] = this.columnDefinitions.map((col) => col.name);

  public ngOnInit(): void {
    const studentId$ = combineLatest(this.route.pathFromRoot.map((route) => route.paramMap)).pipe(
      map((paramMaps): string => {
        for (let i = paramMaps.length - 1; i >= 0; i--) {
          const studentId = paramMaps[i].get('studentId');
          if (studentId) {
            return studentId;
          }
        }

        return '';
      }),
      distinctUntilChanged()
    );

    const academicYearId$ = this.academicYearIdInput$.pipe(distinctUntilChanged());

    combineLatest([studentId$, academicYearId$])
      .pipe(
        switchMap(([studentId, academicYearId]: [string, number | null]) => {
          if (!studentId) {
            return of([] as StudentLedgerTransaction[]);
          }

          this.spinnerService.showSpinner();

          return this.awardsService.getStudentLedger(studentId, academicYearId).pipe(
            catchError((error: HttpErrorResponse) => {
              const errorMessage = error?.error?.message || error?.message || '';
              if (errorMessage) {
                this.toastMessageService.showToast(ToastMessageType.Error, errorMessage);
              }
              return of([] as StudentLedgerTransaction[]);
            }),
            // Also fires when a new student/academic year cancels this request, so the
            // reference-counted spinner stays balanced.
            finalize(() => this.spinnerService.hideSpinner())
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      // Upstream catchError already handles failures (toast + empty array fallback); cannot error here.
      .subscribe((transactions: StudentLedgerTransaction[]) => {
        this.transactions = (transactions ?? []).map((transaction) => this.mapTransaction(transaction));
        this.cdr.detectChanges();
      });
  }

  private mapTransaction(transaction: StudentLedgerTransaction): StudentTransaction {
    return {
      distributionDate: transaction.distributionDate ? new Date(transaction.distributionDate) : null,
      academicYear: transaction.academicYear ?? '',
      school: transaction.school ?? '',
      program: transaction.program ?? '',
      term: transaction.term ?? '',
      distributedAmount: transaction.distributedAmount ?? 0,
      refundAmount: transaction.refundAmount ?? null,
      refundDate: transaction.refundDate ? new Date(transaction.refundDate) : null
    };
  }
}
